import React, { useCallback, memo } from 'react';
import isEqual from 'lodash/isEqual';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import type { MutationUpdaterFn } from 'apollo-client';
import { cssMap, cx } from '@compiled/react';

import Lozenge from '@atlaskit/lozenge';
import ShortcutIcon from '@atlaskit/icon/core/link-external';
import { Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	EDITOR_ONBOARDING_BLUEPRINT_KEY,
	EDITOR_ONBOARDING_BLUEPRINT_KEY_LIVE_DOCS_ENABLED,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { TemplateIcon } from '@confluence/template-icon/entry-points/TemplateIcon.ts';

import { Star } from './Star.tsx';
import {
	Title,
	Description,
	ItemContainer,
	AvatarContainer,
	TitleContainer,
	DescriptionContainer,
} from './TemplateCardStyles.tsx';

export type TemplateCardTemplate = {
	name: string | null;
	description: string | null;
	iconURL: string | null;
	darkModeIconURL?: string | null;
	styleClass: string | null;
	itemModuleCompleteKey: string | null;
	blueprintModuleCompleteKey: string | null;
	contentBlueprintId: string | null;
	templateId: string | null;
	isFavourite?: boolean | null;
	recommendationRank?: number | null;
};

export type TemplateCardProps<T extends TemplateCardTemplate> = {
	template: T;
	onItemClick: (template: T, additionalStatAttributes: object) => void;
	isActive?: boolean;
	isDisabled?: boolean;
	onStarClick?: MutationUpdaterFn | undefined;
};

// React.memo loses generic typings: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087#issuecomment-542793243
const typedMemo: <T>(c: T, propsAreEqual?: (prevProps: T, nextProps: T) => boolean) => T = memo;

const i18n = defineMessages({
	tutorial: {
		id: 'template-card.onboarding.tutorial',
		defaultMessage: 'Tutorial',
		description: 'Indicating the current template card to be a tutorial',
	},
});

const styles = cssMap({
	avatarItem: {
		display: 'flex',
		alignItems: 'center',
		borderStyle: 'solid',
		borderWidth: token('border.width.selected'),
		borderColor: 'transparent',
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		padding: token('space.100'),
		width: '100%',
		textAlign: 'left',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:focus + [data-styled-selector="template-card.ItemContainer"]': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'[data-styled-selector="template-card.StarContainer"]': {
				opacity: 1,
			},
		},
	},
	avtarItemActive: {
		backgroundColor: token('color.background.selected'),
	},
	avatarItemTextWrapper: {
		maxWidth: '100%',
		flex: '1 1 100%',
		paddingInlineStart: token('space.100'),
	},
	avatarItemPrimaryText: {
		color: token('color.text'),
	},
	avatarItemSecondaryText: {
		color: token('color.text.subtlest'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		font: token('font.body'),
	},
});

export const TemplateCard = typedMemo(
	<T extends TemplateCardTemplate>({
		template,
		onItemClick,
		onStarClick,
		isDisabled,
		isActive,
	}: TemplateCardProps<T>) => {
		const title = template.name || '';
		const description = template.description || '';
		const recommendationRank = template.recommendationRank || null;
		const moduleKey = template.blueprintModuleCompleteKey || '';
		let titleComponent = (
			<TitleContainer>
				<Title>{title}</Title>
			</TitleContainer>
		);
		let descriptionComponent = <Description>{description}</Description>;

		const isOnboardingTemplate =
			moduleKey === EDITOR_ONBOARDING_BLUEPRINT_KEY ||
			moduleKey === EDITOR_ONBOARDING_BLUEPRINT_KEY_LIVE_DOCS_ENABLED;

		if (isOnboardingTemplate) {
			titleComponent = (
				<TitleContainer>
					<Title>{title}</Title>
					<Lozenge testId="tutorial-lozenge" appearance="new">
						<FormattedMessage {...i18n.tutorial} />
					</Lozenge>
				</TitleContainer>
			);
			descriptionComponent = (
				<DescriptionContainer>
					<Description>{description}</Description>
					<ShortcutIcon label="" testId="template-card-shortcut-icon" />
				</DescriptionContainer>
			);
		}

		const onClick = useCallback(() => {
			onItemClick && onItemClick(template, { recommendationRank });
		}, [onItemClick, template, recommendationRank]);

		// @todo CTE-337 remove isDisabled check for active highlight
		// when callback for close and create for dialog and drawer are in place
		const active = isActive && isDisabled;

		return (
			<AvatarContainer>
				<Pressable
					xcss={cx(styles.avatarItem, active && styles.avtarItemActive)}
					onClick={onClick}
					isDisabled={isDisabled}
				>
					<TemplateIcon isDisabled={isDisabled} template={template} />
					<div css={styles.avatarItemTextWrapper}>
						<div css={styles.avatarItemPrimaryText}>{titleComponent}</div>
						<div css={styles.avatarItemSecondaryText}>{descriptionComponent}</div>
					</div>
				</Pressable>
				{isOnboardingTemplate ? null : (
					<ItemContainer>
						<Star isDisabled={isDisabled} template={template} onStarClick={onStarClick} />
					</ItemContainer>
				)}
			</AvatarContainer>
		);
	},
	isEqual,
);
